import React, { useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Typography,
    Stack,
    Box,
    Chip,
    Alert,
    MenuItem,
    TextField
} from '@mui/material';
import { AccessLogHelper } from '../../utils/helpers/access-log.helper';
import AnimateButton from '../@extended/AnimateButton';
import { OtcStatus } from '../../utils/constants/otc-status';
import { capitalizeFirstLetter } from '../../utils/Helpers';
import { Loader } from '../index';
import AccessControlService from '../../services/access-control.service';
import SnackbarNotify from '../../utils/snackbar/SnackbarNotify';

const InfoRow = ({ label, value, color = 'text.primary' }) => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Typography
            variant="subtitle1"
            fontWeight={600}
            color="text.secondary"
            sx={{ minWidth: 80 }}
        >
            {label}
        </Typography>
        <Typography
            variant="subtitle1"
            fontWeight={500}
            color={color}
            textTransform="capitalize"
        >
            {value}
        </Typography>
    </Stack>
);

// The gate is never guessed: granting opens a physical gate, so without a kiosk call the
// operator has to pick one (Rapallo's Main Gate guard used to open The Enclave, the
// first kiosk in org.hardware). The calling kiosk, or an org's only kiosk, is preselected.
const getInitialPanelId = (panel, kiosks) => {
    if (panel && kiosks.some(kiosk => kiosk.id === panel.id)) return panel.id;
    if (kiosks.length === 1) return kiosks[0].id;
    return '';
};

const ConsumeOtcModal = ({ onClose, otc, panel, kiosks = [] }) => {
    const [loading, setLoading] = useState(false);
    const [selectedPanelId, setSelectedPanelId] = useState(() =>
        getInitialPanelId(panel, kiosks)
    );

    if (!otc) return null;

    const isFutureStart = otc.starts_at && otc.starts_at.toDate() > new Date();
    const effectiveStatus = isFutureStart ? OtcStatus.INACTIVE : otc.status;

    const handleGrantAccess = async () => {
        setLoading(true);
        try {
            const payload = {
                org_id: otc.org_id,
                panel_id: selectedPanelId,
                code: otc.code
            };

            await AccessControlService.processVirtualGuardOtcAttempt(payload);
            SnackbarNotify.success('Access has been granted!');
            onClose();
        } catch (error) {
            SnackbarNotify.error(
                `Failed to grant access: ${error?.message || 'Unexpected error'}`
            );
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <Loader />}
            <DialogTitle>One Time Code</DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3, maxWidth: 500 }}>
                <Grid container spacing={3} justifyContent="center">
                    <Grid item xs={12} textAlign="center">
                        <Box
                            sx={{
                                display: 'inline-block',
                                px: 4,
                                py: 2,
                                borderRadius: 2,
                                bgcolor: 'primary.lighter'
                            }}
                        >
                            <Typography
                                variant="h2"
                                fontWeight={700}
                                color="primary"
                                sx={{ letterSpacing: 4 }}
                            >
                                {otc.code}
                            </Typography>
                        </Box>
                        <Box mt={2}>
                            <Chip
                                label={capitalizeFirstLetter(effectiveStatus)}
                                color={
                                    effectiveStatus === OtcStatus.ACTIVE
                                        ? 'primary'
                                        : 'error'
                                }
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Stack spacing={1.5}>
                            <InfoRow label="Name:" value={otc.name} />
                            <InfoRow label="Creator:" value={otc.createdBy} />
                            <InfoRow
                                label="Starts:"
                                value={AccessLogHelper.formatAccessCodeTime(
                                    otc.starts_at.toDate()
                                )}
                                color="text.primary"
                            />
                            <InfoRow
                                label="Expires:"
                                value={AccessLogHelper.formatAccessCodeTime(
                                    otc.expires_at.toDate()
                                )}
                            />
                        </Stack>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Gate to open"
                            value={selectedPanelId}
                            disabled={loading || !kiosks.length}
                            onChange={event => setSelectedPanelId(event.target.value)}
                            helperText={
                                kiosks.length
                                    ? 'Granting access opens this gate.'
                                    : 'This organization has no kiosk to open.'
                            }
                        >
                            {kiosks.map(kiosk => (
                                <MenuItem key={kiosk.id} value={kiosk.id}>
                                    {kiosk.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Alert severity="warning" sx={{ fontSize: 13 }}>
                            This Access Code is valid for <b>one time use only</b>.
                            Granting Access will immediately invalidate this code.
                        </Alert>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} justifyContent="flex-end" width="100%">
                    <AnimateButton>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={
                                !(effectiveStatus === OtcStatus.ACTIVE) ||
                                !selectedPanelId ||
                                loading
                            }
                            onClick={handleGrantAccess}
                        >
                            Grant Access
                        </Button>
                    </AnimateButton>
                </Stack>
            </DialogActions>
        </>
    );
};

export default ConsumeOtcModal;
